// all fields documented on https://data.npolar.no/dataset/79d4b7c7-3bee-40a6-975f-f9ed4d253c44

export interface OpenSearchResponse<T> {
  hits: {
    hits: OpenSearchHit<T>[];
  };
}

export interface OpenSearchHit<T> {
  _index: 'weather-troll-station' | 'weather-troll-airfield';
  _id: string; // uuid
  _source: T;
}

export interface StationWeatherMeasurement {
  measured: string;
  DD0_0: number; // Mean wind direction during the last 10 minutes in degrees.
  FG0_0: number; // Wind speed maximum 3 second gust during the last hour in m/s.
  FF0_0: number; // Wind speed 10-minute vector average in m/s. (10 meters)
  FF0_2: number; // Wind speed 10-minute vector average in m/s. (2 meters)
  TA0_0: number; // Air temperature mean during the last minute in °C.
  UU0_0: number; // Relative humidity in percent.
  PH0_0: number; // Sea level pressure, adjusted using the observed temperature in hPa.
}

export interface AirfieldWeatherMeasurement {
  measured: string;
  DD0_6: number; // Mean wind direction during the last 10 minutes in degrees.
  FG0_6: number; // Wind speed maximum 3 second gust during the last hour in m/s.
  FF0_6: number; // Wind speed maximum 10 minutes mean during the last hour in m/s.
  TA0_0: number; // Air temperature mean during the last minute in °C.
  UU0_0: number; // Relative humidity in percent.
  PH0_0: number; // Sea level pressure, adjusted using the observed temperature in hPa.
}

export class WeatherMeasurement {
  static readonly TROLL_MAGNETIC_VARIATION = 23;
  static readonly KNOTS_IN_MS = 1.943844;

  measuredString: string;
  measured: Date;
  airTemperature: number;
  airHumidity: number;
  dewPoint: number;
  windDirectionTrue: number;
  windDirectionMagnetic: number;
  windSpeedMs: number;
  windSpeedKnots: number;
  windGustMs: number;
  windGustKnots: number;
  pressure: number;

  constructor(data: OpenSearchHit<StationWeatherMeasurement | AirfieldWeatherMeasurement>) {
    this.measuredString = data._source.measured;
    this.measured = new Date(this.measuredString);
    this.airTemperature = data._source.TA0_0;
    this.airHumidity = data._source.UU0_0;
    this.dewPoint = this.getDewPointTemperature(this.airTemperature, this.airHumidity);

    if (data._index === "weather-troll-station") {
      const station = data._source as StationWeatherMeasurement;
      this.windDirectionTrue = station.DD0_0;
      this.windSpeedMs = station.FF0_0;
      this.windGustMs = station.FG0_0;
      this.pressure = station.PH0_0;
    } else {
      const airfield = data._source as AirfieldWeatherMeasurement;
      this.windDirectionTrue = airfield.DD0_6;
      this.windSpeedMs = airfield.FF0_6;
      this.windGustMs = airfield.FG0_6;
      this.pressure = airfield.PH0_0;
    }

    this.windSpeedKnots = this.windSpeedMs * WeatherMeasurement.KNOTS_IN_MS;
    this.windGustKnots = this.windGustMs * WeatherMeasurement.KNOTS_IN_MS;
    this.windDirectionMagnetic = this.getMagneticHeading(this.windDirectionTrue,
      WeatherMeasurement.TROLL_MAGNETIC_VARIATION);
  }

  private getMagneticHeading(trueHeading: number, magneticVariation: number): number {
    return (trueHeading - magneticVariation + 360) % 360;
  }

  // Stephen Hudson:
  // duggpunkt kan regnes ut fra T (°C) og RH (%) med:
  // γ = ln(RH / 100) + (17.625 × T) / (243.04 + T)
  // Td = 243.04 × γ / (17.625 – γ)
  // Psi(γ) brukes kun i formelen.
  private getDewPointTemperature(temperature: number, relativeHumidity: number): number {
    const y = Math.log(relativeHumidity / 100) + (17.625 * temperature) / (243.04 + temperature)
    return 243.04 * y / (17.625 - y)
  }
}
